import React, { memo, useCallback } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { Navigate, useLocation } from 'react-router-dom'
import tw from 'twin.macro'
import { AuthScreen } from 'src/components/auth/AuthScreen'
import { Button } from 'src/components/shared/Button'
import { ADMIN_ROUTES } from 'src/constants/routes'

/**
 * The app's sign-in screen, and the target of the WorkOS Initiate Login URL for sign-ins that
 * start outside the app.
 *
 * The page the user was trying to reach rides along in the OAuth `state` rather than in router
 * state: AuthKit sends the browser out to WorkOS and back to the app origin, and a full page load
 * takes any in-memory router state with it.
 */
const LoginPageComponent = () => {
  const { isLoading, user, signIn } = useAuth()
  const location = useLocation()

  const requestedPath = (location.state as { from?: string } | null)?.from

  const handleSignIn = useCallback(() => {
    signIn(requestedPath ? { state: { returnTo: requestedPath } } : undefined)
  }, [signIn, requestedPath])

  // Reached with a live session — a bookmark, or a second tab that signed in meanwhile.
  if (!isLoading && user) {
    return <Navigate to={requestedPath ?? ADMIN_ROUTES.LAUNCHER_URL} replace />
  }

  return (
    <AuthScreen
      dataTestId="admin-login-page"
      title="GigSafe Admin"
      description="Sign in with your GigSafe account to continue."
    >
      <Button
        size="medium"
        color="dark"
        className={tw`mt-2 w-full`}
        disabled={isLoading}
        onClick={handleSignIn}
        data-testid="admin-login-sign-in-button"
      >
        Sign in
      </Button>
    </AuthScreen>
  )
}

export const LoginPage = memo(LoginPageComponent)
